import { AccountStatementUpload } from "./components/AccountStatementUpload";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { Checkbox } from "@groot/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Account, Statement } from "@/types";
import dayjs from "dayjs";
import { ArrowLeft, BoxSelect, Eye, FileText, Plus, X, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useConfirm } from "@groot/ui/primitives";

export function AccountStatements() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<Account | null>(null);
  const [statements, setStatements] = useState<Statement[]>([]);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [selectedStatementIds, setSelectedStatementIds] = useState<number[]>([]);
  const [bulkExtracting, setBulkExtracting] = useState(false);
  const [bulkExtractDialogOpen, setBulkExtractDialogOpen] = useState(false);
  const [bulkAutoUpload, setBulkAutoUpload] = useState(true);
  const [bulkRenameFile, setBulkRenameFile] = useState(false);
  const [bulkIgnorePagesFromEnd, setBulkIgnorePagesFromEnd] = useState<number | undefined>(
    undefined,
  );
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    if (id) {
      loadAccount();
      loadStatements();
    }
  }, [id]);

  useEffect(() => {
    const extractorConfig = account?.meta?.statementExtractor as
      | Record<string, unknown>
      | undefined;
    const defaultPages = extractorConfig?.defaultPagesToIgnoreFromEnd;
    if (typeof defaultPages === "number") {
      setBulkIgnorePagesFromEnd(defaultPages);
    }
  }, [account]);

  const loadAccount = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getAccount(Number(id));
      setAccount(data);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load account",
      });
      navigate("/accounts");
    } finally {
      setLoading(false);
    }
  };

  const loadStatements = async () => {
    if (!id) return;
    try {
      setStatementsLoading(true);
      const data = await apiClient.getStatementsByAccountId(Number(id));
      setStatements(data);
    } catch (error) {
      console.error("Failed to load statements:", error);
    } finally {
      setStatementsLoading(false);
    }
  };

  const toggleStatementSelect = (statementId: number) => {
    setSelectedStatementIds((prev) =>
      prev.includes(statementId) ? prev.filter((s) => s !== statementId) : [...prev, statementId],
    );
  };

  const selectAllStatements = () => {
    setSelectedStatementIds((prev) =>
      prev.length === statements.length ? [] : statements.map((s) => s.id),
    );
  };

  const handleBulkExtract = async () => {
    if (selectedStatementIds.length === 0) return;
    try {
      setBulkExtracting(true);
      const results = await Promise.allSettled(
        selectedStatementIds.map((sid) =>
          apiClient.extractStatement(sid, {
            autoUpload: bulkAutoUpload,
            accountId: account?.id,
            renameFile: bulkRenameFile,
            ignorePagesFromEnd: bulkIgnorePagesFromEnd,
          }),
        ),
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;
      toast({
        title: "Queued",
        description: `Extraction queued for ${succeeded} statements${failed ? `, ${failed} failed` : ""}`,
      });
      setSelectedStatementIds([]);
      await loadStatements();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to queue extraction",
        variant: "destructive",
      });
    } finally {
      setBulkExtracting(false);
      setBulkExtractDialogOpen(false);
    }
  };

  const getStatementBadge = (st: Statement) => {
    // `Badge` has no info/success tones; `variant={null}` renders the same
    // untoned pill those unknown variant names always produced.
    if (st.status === "processing") {
      return <Badge variant={null}>Processing</Badge>;
    }
    const extractedData = st.extractedData as {
      transactions?: unknown[];
    } | null;
    const txs = Array.isArray(extractedData?.transactions) ? extractedData.transactions : [];
    if (txs.length > 0) {
      return <Badge variant={null}>Extracted</Badge>;
    }
    return null;
  };

  if (loading) {
    return (
      <Page maxWidth="5xl">
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (!account) {
    return null;
  }

  return (
    <>
      <Page maxWidth="5xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(`/accounts/${id}`)}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {account.accountName}
          </Button>
          <PageHeader
            title="Statements"
            description={`Manage statements for ${account.accountName}`}
            actions={
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <Button variant="outline" size="sm" onClick={selectAllStatements} className="gap-2">
                  <BoxSelect className="w-4 h-4" />
                  {selectedStatementIds.length === statements.length && statements.length > 0
                    ? "Unselect all"
                    : "Select all"}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setBulkExtractDialogOpen(true)}
                  disabled={selectedStatementIds.length === 0 || bulkExtracting}
                  className="gap-2"
                >
                  Extract whole PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowUploadForm(!showUploadForm)}
                  className="gap-2"
                >
                  {showUploadForm ? (
                    <>
                      <X className="w-4 h-4" />
                      Cancel
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
            }
          />
        </div>

        {showUploadForm && (
          <div className="mb-6">
            <AccountStatementUpload
              accountId={account.id}
              onUploadComplete={() => {
                loadStatements();
                setShowUploadForm(false);
              }}
            />
          </div>
        )}

        {statementsLoading ? (
          <LoadingSpinner className="py-8" />
        ) : statements.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No statements uploaded yet
          </div>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {statements.map((statement) => (
              <div
                key={statement.id}
                className="flex items-center justify-between p-4 hover:bg-muted"
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedStatementIds.includes(statement.id)}
                    onCheckedChange={() => toggleStatementSelect(statement.id)}
                  />
                  <FileText className="size-5 text-muted-foreground/70" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {statement.month
                        ? dayjs(statement.month, "YYYY-MM").format("MMMM YYYY")
                        : statement.fileName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {dayjs(statement.uploadedAt).format("MMM D, YYYY")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatementBadge(statement)}
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/statements/${statement.id}`}>
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: "Delete Statement",
                          description:
                            "Are you sure you want to delete this statement? This action cannot be undone and will delete the associated file.",
                          destructive: true,
                        })
                      ) {
                        try {
                          await apiClient.deleteStatement(statement.id);
                          toast({
                            title: "Success",
                            description: "Statement deleted successfully",
                          });
                          loadStatements();
                        } catch {
                          toast({
                            title: "Error",
                            description: "Failed to delete statement",
                            variant: "destructive",
                          });
                        }
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Page>

      <Dialog open={bulkExtractDialogOpen} onOpenChange={setBulkExtractDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm bulk extraction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Do you want to automatically upload extracted transactions after extraction for the
              selected statements?
            </p>
            <div className="flex items-center gap-2">
              <Checkbox
                id="account-bulk-auto-upload-checkbox"
                checked={bulkAutoUpload}
                onCheckedChange={(v) => setBulkAutoUpload(Boolean(v))}
              />
              <label htmlFor="account-bulk-auto-upload-checkbox" className="text-sm">
                Auto-upload extracted transactions
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="account-bulk-rename-file-checkbox"
                checked={bulkRenameFile}
                onCheckedChange={(v) => setBulkRenameFile(Boolean(v))}
              />
              <label htmlFor="account-bulk-rename-file-checkbox" className="text-sm">
                Rename file to YYYY-MM.pdf
              </label>
            </div>
            <div>
              <Label htmlFor="account-bulk-ignore-pages-end" className="text-sm">
                Ignore pages from end
              </Label>
              <Input
                id="account-bulk-ignore-pages-end"
                type="number"
                min="0"
                placeholder="0"
                value={bulkIgnorePagesFromEnd ?? ""}
                onChange={(e) =>
                  setBulkIgnorePagesFromEnd(e.target.value ? Number(e.target.value) : undefined)
                }
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Skip last X pages (e.g., T&C, ads)
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkExtractDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkExtract} disabled={bulkExtracting}>
                {bulkExtracting ? <LoadingSpinner className="size-4" /> : null}
                Extract
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
